// src/pages/Triage.tsx
import { useState, useMemo, useCallback } from 'react'
import { ArrowLeft, Sparkles, Zap, Trash2, Check, RotateCcw } from 'lucide-react'
import { useTasks } from '../stores/useTasks'
import { api } from '../services/api'
import type { Task } from '../types'

type FilterType = 'all' | 'unclassified' | 'signal' | 'noise'

function fmtMins(mins: number) {
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60); const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h${m}m`
}

function TriageTile({ task, classifying, onClassify, onDone, onDelete, onEstimate }:
  { task: Task; classifying: boolean; onClassify: (id: string, type: 'signal' | 'noise') => void; onDone: (id: string) => void; onDelete: (id: string) => void; onEstimate: (id: string, mins: number | null) => void }
) {
  const [expanded, setExpanded] = useState(false)
  const [editingEst, setEditingEst] = useState(false)
  const [estText, setEstText] = useState(task.estimated_mins ? fmtMins(task.estimated_mins) : '')

  const typeColor = task.type === 'signal' ? 'var(--signal)' : task.type === 'noise' ? 'var(--noise)' : 'var(--text-dim)'
  const leftBorder = task.type === 'signal' ? 'var(--signal)' : task.type === 'noise' ? 'var(--noise)' : 'var(--border)'

  function submitEst() {
    const s = estText.trim().toLowerCase()
    let mins: number | null = null
    const hm = s.match(/^(\d+)\s*h\s*(\d+)\s*m$/); if (hm) mins = parseInt(hm[1]) * 60 + parseInt(hm[2])
    const h = s.match(/^(\d+)\s*h$/); if (h) mins = parseInt(h[1]) * 60
    const m = s.match(/^(\d+)\s*m$/); if (m) mins = parseInt(m[1])
    if (!isNaN(parseInt(s)) && parseInt(s) > 0) mins = parseInt(s)
    setEstimate: onEstimate(task.id, mins); setEditingEst(false)
  }

  return (
    <div className={`task-card ${task.type === 'signal' ? 'signal' : task.type === 'noise' ? 'noise' : ''}`}
      style={{ opacity: classifying ? 0.5 : 1, borderLeftColor: leftBorder }}>
      <div className="task-card-inner" onClick={() => setExpanded(e => !e)}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sm)', marginBottom: 'var(--sm)' }}>
          <p className="task-card-text" style={{ color: task.done ? 'var(--text-dim)' : 'var(--text)', textDecoration: task.done ? 'line-through' : 'none', flex: 1 }}>
            {task.text}
          </p>
          {task.type !== 'unclassified' && (
            <span className="badge" style={{ background: `color-mix(in srgb, ${typeColor} 18%, transparent)`, color: typeColor, border: `1px solid color-mix(in srgb, ${typeColor} 40%, transparent)`, flexShrink: 0 }}>
              {task.type === 'signal' ? '◈' : '◉'} {task.type}
            </span>
          )}
        </div>
        <div className="task-card-actions">
          {task.type !== 'signal' && (
            <button className="badge badge-signal" style={{ cursor: 'pointer', border: 'none' }}
              onClick={e => { e.stopPropagation(); onClassify(task.id, 'signal') }}>◈ Signal</button>
          )}
          {task.type !== 'noise' && (
            <button className="badge badge-noise" style={{ cursor: 'pointer', border: 'none' }}
              onClick={e => { e.stopPropagation(); onClassify(task.id, 'noise') }}>◉ Noise</button>
          )}
          <div style={{ flex: 1 }} />
          <button className="badge badge-muted" style={{ cursor: 'pointer', border: 'none' }}
            onClick={e => { e.stopPropagation(); setEditingEst(true) }}>
            {task.estimated_mins ? `~${fmtMins(task.estimated_mins)}` : '+ est'}
          </button>
          <button className="btn btn-icon" style={{ background: 'transparent', width: 'auto', padding: 4 }}
            onClick={e => { e.stopPropagation(); onDone(task.id) }}>
            {task.done ? <RotateCcw size={15} color="var(--text-dim)" /> : <Check size={15} color="var(--signal)" />}
          </button>
          <button className="btn btn-icon" style={{ background: 'transparent', width: 'auto', padding: 4 }}
            onClick={e => { e.stopPropagation(); onDelete(task.id) }}>
            <Trash2 size={15} color="var(--noise)" />
          </button>
        </div>
      </div>
      {expanded && task.ai_reason && (
        <div className="task-card-reason animate-fade-in">"{task.ai_reason}"</div>
      )}
      {editingEst && (
        <div style={{ padding: '0 var(--md) var(--md)', borderTop: '1px solid var(--border)', paddingTop: 'var(--sm)' }} className="animate-fade-in">
          <div style={{ display: 'flex', gap: 'var(--sm)' }}>
            <input autoFocus className="input" style={{ flex: 1, padding: 'var(--sm) var(--md)' }}
              value={estText} onChange={e => setEstText(e.target.value)}
              placeholder="30m · 1h · 1h30m"
              onKeyDown={e => e.key === 'Enter' && submitEst()}
              onBlur={submitEst} />
            <button className="btn btn-primary" style={{ width: 36, height: 36, padding: 0 }} onClick={submitEst}>
              <Check size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

interface TriageProps { onNavigate: (page: string) => void }

export default function TriagePage({ onNavigate }: TriageProps) {
  const { tasks, classify, markDone, delete: del, setEstimate, add } = useTasks()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')
  const [classifyingIds, setClassifyingIds] = useState<Set<string>>(new Set())
  const [classifyAllBusy, setClassifyAllBusy] = useState(false)
  const [quickAdd, setQuickAdd] = useState('')

  const filtered = useMemo(() => tasks.filter(t => {
    const matchFilter = filter === 'all' || t.type === filter
    const matchSearch = !search || t.text.toLowerCase().includes(search.toLowerCase())
    return matchFilter && matchSearch
  }), [tasks, filter, search])

  const unclassified = useMemo(() => filtered.filter(t => t.type === 'unclassified'), [filtered])
  const signals      = useMemo(() => filtered.filter(t => t.type === 'signal'),       [filtered])
  const noises       = useMemo(() => filtered.filter(t => t.type === 'noise'),        [filtered])

  const classifiedCount = signals.length + noises.length
  const snrPct = classifiedCount > 0 ? Math.round((signals.length / classifiedCount) * 100) : 0
  const snrColor = snrPct >= 75 ? 'var(--signal)' : snrPct >= 50 ? 'var(--amber)' : 'var(--noise)'

  const handleClassify = useCallback((id: string, type: 'signal' | 'noise') => {
    classify(id, type, 'User manual')
  }, [classify])

  const handleClassifyAll = useCallback(async () => {
    const pending = tasks.filter(t => t.type === 'unclassified')
    if (!pending.length) { alert('Nothing to classify'); return }
    setClassifyAllBusy(true)
    setClassifyingIds(new Set(pending.map(t => t.id)))
    await Promise.all(pending.map(async task => {
      try {
        const result = await api.classify(task.text)
        if (result?.type) classify(task.id, result.type, result.reason ?? null)
      } catch { /* leave as unclassified */ }
      finally {
        setClassifyingIds(prev => { const n = new Set(prev); n.delete(task.id); return n })
      }
    }))
    setClassifyAllBusy(false)
  }, [tasks, classify])

  const handleQuickAdd = async () => {
    if (!quickAdd.trim()) return
    await add(quickAdd.trim()); setQuickAdd('')
  }

  function renderSection(title: string, icon: string, list: Task[]) {
    const color = title === 'Signal' ? 'var(--signal)' : title === 'Noise' ? 'var(--noise)' : 'var(--text-muted)'
    return (
      <div style={{ marginTop: 'var(--md)' }}>
        <div className="section-header">
          <span className="label" style={{ color }}>{icon} {title}</span>
          <span className="badge badge-muted">{list.length}</span>
        </div>
        {list.length === 0
          ? <p className="body-md text-dim" style={{ marginTop: 'var(--xs)' }}>
              {title === 'Unclassified' ? '✓ All caught up — nothing to triage' : 'None yet'}
            </p>
          : list.map(t => (
            <TriageTile key={t.id} task={t}
              classifying={classifyingIds.has(t.id)}
              onClassify={handleClassify}
              onDone={id => markDone(id, !tasks.find(t2 => t2.id === id)?.done)}
              onDelete={del}
              onEstimate={setEstimate}
            />
          ))
        }
      </div>
    )
  }

  return (
    <div className="page page-top">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sm)', marginBottom: 'var(--lg)' }}>
        <button className="btn btn-icon" style={{ background: 'transparent', width: 'auto', padding: 'var(--xs)' }} onClick={() => onNavigate('inbox')}>
          <ArrowLeft size={20} color="var(--text-muted)" />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--xs)' }}>
            <Zap size={18} color="var(--accent)" />
            <h1 className="display-md text-primary">Triage Mode</h1>
          </div>
          <p className="label" style={{ marginTop: 2 }}>
            <span className="text-muted">{unclassified.length} unclassified · </span>
            <span className="text-signal">{signals.length} signal</span>
            <span className="text-muted"> · </span>
            <span className="text-noise">{noises.length} noise</span>
            {classifiedCount > 0 && <span style={{ color: snrColor }}> · {snrPct}% SNR</span>}
          </p>
        </div>
        <button
          className="btn btn-primary btn-sm"
          style={{ width: 'auto', gap: 4 }}
          onClick={handleClassifyAll}
          disabled={classifyAllBusy}
        >
          <Sparkles size={13} />
          {classifyAllBusy ? 'Working…' : 'Classify All'}
        </button>
      </div>

      {/* Quick add */}
      <div style={{ display: 'flex', gap: 'var(--sm)', alignItems: 'center', background: 'var(--card-low)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '0 var(--md)', marginBottom: 'var(--md)' }}>
        <input
          id="triage-quick-add"
          className="input"
          style={{ background: 'transparent', border: 'none', flex: 1, padding: 'var(--md) 0' }}
          value={quickAdd}
          onChange={e => setQuickAdd(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleQuickAdd()}
          placeholder="Add a task straight to triage…"
        />
        <button className="btn btn-primary btn-icon" style={{ width: 28, height: 28, padding: 0, flexShrink: 0 }} onClick={handleQuickAdd}>+</button>
      </div>

      {/* Search */}
      <input
        id="triage-search"
        className="input"
        style={{ marginBottom: 'var(--sm)' }}
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Filter tasks…"
      />

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 'var(--sm)', marginBottom: 'var(--md)', flexWrap: 'wrap' }}>
        {(['all', 'unclassified', 'signal', 'noise'] as FilterType[]).map(f => (
          <button
            key={f}
            className={`badge ${filter === f ? 'badge-accent' : 'badge-muted'}`}
            style={{ cursor: 'pointer', border: `1px solid ${filter === f ? 'color-mix(in srgb, var(--accent) 45%, transparent)' : 'var(--border)'}` }}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'unclassified' ? '⬜ Unclassified' : f === 'signal' ? '◈ Signal' : '◉ Noise'}
          </button>
        ))}
      </div>

      {/* Sections */}
      {renderSection('Unclassified', '⬜', unclassified)}
      {renderSection('Signal', '◈', signals)}
      {renderSection('Noise', '◉', noises)}
    </div>
  )
}
